import React from 'react'
import type { PresentationBlock } from 'paintscout'

import { ButtonSelectOption, ColorOption } from '../../components'
import SocialLinkRow from './SocialLinkRow'
import AddSocialLinkRow from './AddSocialLinkRow'
import VerticalAlignTopIcon from '@material-ui/icons/VerticalAlignTop'
import VerticalAlignCenterIcon from '@material-ui/icons/VerticalAlignCenter'

interface SocialLinksOptionsProps {
  block: PresentationBlock
  presetColors: string[]
  handleOptionFieldChange: (field: string, value: any) => void
}

function SocialLinksOptions(props: SocialLinksOptionsProps) {
  const { block, presetColors, handleOptionFieldChange } = props
  // blocks saved before the icon color existed have no `color` — '' is the same 'follow the
  // theme accent' state a cleared picker leaves behind, and it keeps the clear button hidden
  const { fontSize, links = [], alignment, color = '' } = block.fields

  const handleLinkChange = (value: string, index: number) => {
    handleOptionFieldChange(`fields.links[${index}].href`, value)
  }

  const handleAddLink = (label: string) => {
    handleOptionFieldChange('fields.links', [...links, { label, href: '' }])
  }

  const handleRemoveLink = (index: number) => {
    handleOptionFieldChange(
      'fields.links',
      links.filter((_link, i) => i !== index)
    )
  }

  const labelWidth = links.reduce((max, link) => Math.max(max, link.label.length), 0)

  const linkOptions = links.map((link, index) => {
    return {
      Component: (
        <SocialLinkRow
          key={index}
          label={link.label}
          labelWidth={labelWidth}
          value={link.href}
          onChange={(value) => handleLinkChange(value, index)}
          onRemove={() => handleRemoveLink(index)}
        />
      ),
      tab: 'content'
    }
  })

  const options = [
    ...linkOptions,
    {
      Component: <AddSocialLinkRow onAdd={handleAddLink} />,
      tab: 'content'
    },
    {
      Component: (
        <ButtonSelectOption
          label={'Icon Size'}
          value={fontSize}
          buttons={[
            { icon: 'S', value: '2rem' },
            { icon: 'M', value: '3rem' },
            { icon: 'L', value: '4rem' }
          ]}
          onChange={(value) => handleOptionFieldChange('fields.fontSize', value)}
        />
      ),
      tab: 'content'
    },
    {
      Component: (
        <ColorOption
          label={'Icon Color'}
          value={color}
          useClearOption={true}
          helpTextForClear={'Remove Color Override'}
          defaultColor={''}
          clearColor={''}
          presetColors={presetColors}
          onChange={(value) => handleOptionFieldChange('fields.color', value)}
        />
      ),
      tab: 'content'
    },
    {
      Component: (
        <ButtonSelectOption
          label={'Alignment'}
          value={alignment}
          buttons={[
            { icon: <VerticalAlignTopIcon style={{ transform: 'rotate(-90deg)' }} />, value: 'flex-start' },
            { icon: <VerticalAlignCenterIcon style={{ transform: 'rotate(-90deg)' }} />, value: 'center' },
            { icon: <VerticalAlignTopIcon style={{ transform: 'rotate(90deg)' }} />, value: 'flex-end' },
            { icon: 'B', value: 'space-between' },
            { icon: 'A', value: 'space-around' },
            { icon: 'E', value: 'space-evenly' }
          ]}
          onChange={(value) => handleOptionFieldChange('fields.alignment', value)}
        />
      ),
      tab: 'content'
    }
  ]

  return options
}

export default SocialLinksOptions
